import * as legacy28d1 from './break-treatment-legacy-28d1';
import {
  breakEvidenceObject,
  classifyRosterPresumedBreak,
  originalBreakIsPresumed,
  originalBreakHasRecordedMinutes,
  rosterScheduledBreakMinutes,
  type RosterBreakContext,
} from './roster-presumed-break';
export type BreakTreatmentKind =
  | 'UNPAID'
  | 'PAID_COUNTS_AS_TIME_WORKED'
  | 'PAID_NOT_TIME_WORKED'
  | 'NO_BREAK';

export type BreakTreatmentStatus = 'NOT_APPLICABLE' | 'RESOLVED' | 'BLOCKED' | 'AMBIGUOUS';

const BREAK_TREATMENT_RESOLVER_HASH_VERSION = 'break-treatment-v13-unified';
// Only presumed captures change calculation semantics; ordinary V13 hashes stay fixed.
// ts10 (#4750): a paid or below-trigger presumed capture is no longer held.
const ROSTER_PRESUMED_BREAK_RESOLVER_HASH_VERSION = 'break-treatment-v13-roster-presumed-ts10';

/**
 * Which break rules a caller is bound by (release ruling 28 Oct 2026, R71).
 *
 * - `LEGACY_28D1`: production main 28d166931e, kept verbatim in
 *   `./break-treatment-legacy-28d1`. Every tenant whose payroll rebuild is OFF.
 * - `V13`: the 20 Oct ruling / unified v13 rules in this module. Only a tenant
 *   whose rebuild gate (`payrollRebuildEnabled`: mode row AND row 31 install
 *   proof) is ON.
 *
 * Omitted means LEGACY_28D1, so any caller that does not resolve the gate keeps
 * live behaviour. Preflight, recompute, snapshot and cumulative-hours callers
 * must resolve the gate once per tenant and pass the same value everywhere.
 */
export type BreakSemantics = 'LEGACY_28D1' | 'V13';

export const DEFAULT_BREAK_SEMANTICS: BreakSemantics = 'LEGACY_28D1';

export function breakSemanticsForRebuild(rebuildEnabled: boolean): BreakSemantics {
  return rebuildEnabled === true ? 'V13' : 'LEGACY_28D1';
}

export function isV13BreakSemantics(semantics: BreakSemantics | null | undefined): boolean {
  return semantics === 'V13';
}

export const DEFAULT_TIMESHEET_MISSING_BREAK_POLICY = 'RECORDED_ONLY' as const;

export type TimesheetMissingBreakPolicyMode =
  | typeof DEFAULT_TIMESHEET_MISSING_BREAK_POLICY
  | 'ASSUME_RULE_DEFAULT_UNLESS_EXPLICIT_NO_BREAK';

export interface TimesheetMissingBreakPolicyConfig {
  mode: TimesheetMissingBreakPolicyMode;
  effectiveFrom: string | null;
  configuredAt: string | null;
  configuredBy: string | null;
  reason: string | null;
}

export type TimesheetBreakMinutesProvenance =
  | 'APPROVED_FACT_NOT_TAKEN'
  | 'APPROVED_FACT_DURATION'
  | 'APPROVED_FACT_TAKEN_RULE_DEFAULT'
  | 'RECORDED_BREAK_MINUTES'
  | 'ROSTER_PRESUMED'
  | 'PROVIDER_POLICY_RULE_DEFAULT'
  | 'LEGACY_RECORDED_ZERO'
  | 'RESOLVER_RULE_DEFAULT'
  | 'SCHEDULED_BREAK_MINUTES'
  | 'RULE_NO_BREAK_REQUIRED'
  | 'UNRESOLVED'
  | 'NONE';

export type BreakTreatmentSourceKind =
  | 'ROSTER_SHIFT'
  | 'ROSTER_ASSIGNMENT'
  | 'TIMESHEET'
  | 'LEAVE_REQUEST';

export interface BreakTreatmentRuleBlock {
  id?: string | null;
  block_code: string;
  block_kind: string;
  title?: string | null;
  precedence?: number | null;
  automation_status?: string | null;
  required_fact_codes?: string[] | null;
  export_component_codes?: string[] | null;
  parameters?: Record<string, unknown> | null;
}

export interface BreakTreatmentFactInput {
  factCode: string;
  value: unknown;
  status?: string | null;
}

export interface BreakTreatmentSegment {
  segmentKind:
    | 'WORKED'
    | 'UNPAID_BREAK'
    | 'PAID_BREAK_COUNTS_AS_TIME_WORKED'
    | 'PAID_BREAK_NOT_TIME_WORKED';
  minutes: number;
  countsForPayroll: boolean;
  countsAsTimeWorked: boolean;
  countsForLeave: boolean;
  countsForOvertimeThreshold: boolean;
  countsForCareMinutes: boolean;
}

export interface BreakTreatmentSnapshotV1 {
  schemaVersion: 1;
  status: BreakTreatmentStatus;
  treatmentKind: BreakTreatmentKind;
  sourceKind: BreakTreatmentSourceKind;
  ruleBlockId: string | null;
  ruleBlockCode: string | null;
  /** Applicable rule proving this worked span has not reached its break trigger. */
  noBreakRequiredRuleBlockId?: string | null;
  ruleHash: string;
  scheduledBreakMinutes: number;
  actualBreakMinutes: number;
  /** Why the resolver used these actual break minutes. Added without changing schemaVersion 1. */
  actualBreakMinutesProvenance?: TimesheetBreakMinutesProvenance;
  /** Numeric legacy storage is not evidence of a confirmed zero. */
  breakMinutesStatus?: 'UNRESOLVED';
  rawSpanMinutes: number;
  workedMinutes: number;
  payrollPaidBreakMinutes: number;
  payrollDeductibleBreakMinutes: number;
  ordinaryTimeWorkedBreakMinutes: number;
  leaveAccruableBreakMinutes: number;
  overtimeThresholdBreakMinutes: number;
  careMinuteExcludedBreakMinutes: number;
  requiredFactCodes: string[];
  suppliedFactCodes: string[];
  derivedFacts?: Array<{
    factCode: string;
    provenance:
      | 'DERIVED_FROM_RECORDED_BREAK_MINUTES'
      | 'DERIVED_FROM_PROVIDER_MISSING_BREAK_POLICY'
      | 'DERIVED_FROM_APPROVED_BREAK_TAKEN_FACT'
      | 'DERIVED_FROM_RULE_PAID_BREAK_ASSUMPTION';
  }>;
  missingFactCodes: string[];
  blockers: Array<{ code: string; message: string }>;
  segments: BreakTreatmentSegment[];
}

export interface ResolveBreakTreatmentInput {
  /** Structured server capture, independent of source_evidence. V13 only. */
  rosterPresumption?: RosterBreakContext;
  /** A validated correction overlay always wins over the original capture. */
  approvedCorrectionMinutes?: number | null;
  sourceKind: BreakTreatmentSourceKind;
  employeeCategory?: string | null;
  rawSpanMinutes: number;
  scheduledBreakMinutes: number | null | undefined;
  actualBreakMinutes?: number | null;
  actualBreakMinutesProvenance?: TimesheetBreakMinutesProvenance;
  /** The stored row value, before unknown punch provenance is recovered. */
  recordedBreakMinutes?: number | null;
  /** True only after a successful tenant-wide BREAK_TREATMENT/BREAKS read. */
  noTenantBreakRule?: boolean;
  ruleBlocks?: BreakTreatmentRuleBlock[] | null;
  facts?: BreakTreatmentFactInput[] | null;
  finalizationMode?: 'FORECAST' | 'FINAL';
  /**
   * The worker's LIVE payment method, never a stored marker.
   *
   * Break treatment exists to tell the award engine how to price a break. A
   * salary-flat worker never enters award classification — their ordinary pay is
   * materialised once per pay period by the salary-flat synthesiser — so no break
   * determination can change what they are paid. Supplying `SALARY_FLAT` here
   * resolves to NOT_APPLICABLE instead of demanding meal-break facts that cannot
   * affect the outcome.
   *
   * This suppresses only salary pricing determination. Care-minute readers use
   * actual capture evidence independently and exclude recorded breaks even for
   * salaried workers. They do not treat this payroll NOT_APPLICABLE snapshot as
   * evidence that a break was taken or not taken.
   */
  paymentMethod?: string | null;
  /** Omitted = LEGACY_28D1 (see BreakSemantics). */
  breakSemantics?: BreakSemantics;
}

function normalizedText(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function normalizedUpper(value: unknown): string | null {
  return normalizedText(value)?.toUpperCase() ?? null;
}

export function canonicalEmployeeCategory(value: unknown): string | null {
  const normalized = normalizedUpper(value);
  if (!normalized) return null;
  if (normalized === 'DAYWORKER' || normalized === 'DAY_WORKER') return 'DAYWORKER';
  if (
    normalized === 'SHIFTWORKER' ||
    normalized === 'SHIFT_WORKER' ||
    normalized === 'ROTATING_ROSTER' ||
    normalized === 'PERMANENT_NIGHT' ||
    normalized === 'PERMANENT_NIGHT_SHIFT'
  ) {
    return 'SHIFTWORKER';
  }
  return null;
}

function nonNegativeMinutes(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.round(n));
}

function nonNegativeNumber(value: unknown, fallback = 100): number {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, n);
}

function recordText(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function validDateOnly(value: string | null): string | null {
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

export function parseTimesheetMissingBreakPolicy(
  value: unknown,
): TimesheetMissingBreakPolicyConfig {
  const record =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const configuredMode = normalizedUpper(record.mode);
  const mode: TimesheetMissingBreakPolicyMode =
    configuredMode === 'ASSUME_RULE_DEFAULT_UNLESS_EXPLICIT_NO_BREAK'
      ? configuredMode
      : DEFAULT_TIMESHEET_MISSING_BREAK_POLICY;
  return {
    mode,
    effectiveFrom: validDateOnly(recordText(record, 'effectiveFrom')),
    configuredAt: recordText(record, 'configuredAt'),
    configuredBy: recordText(record, 'configuredBy'),
    reason: recordText(record, 'reason'),
  };
}

export function timesheetMissingBreakPolicyApplies(
  policy: TimesheetMissingBreakPolicyConfig,
  eventDate: string,
): boolean {
  return (
    policy.mode === 'ASSUME_RULE_DEFAULT_UNLESS_EXPLICIT_NO_BREAK' &&
    (policy.effectiveFrom === null || eventDate >= policy.effectiveFrom)
  );
}

export interface ResolveTimesheetActualBreakInput {
  eventDate: string;
  storedBreakMinutes: number | null | undefined;
  /** Server-captured attendance or coordinator correction provenance. */
  sourceEvidence?: unknown;
  approvedMealBreakTaken?: boolean | null;
  approvedMealBreakDuration?: number | null;
  policy: TimesheetMissingBreakPolicyConfig;
  /** Capture provenance: a roster/rule default is never an actual break. */
  storedBreakProvenance?: unknown;
  storedCaptureMethod?: unknown;
  /** Omitted = LEGACY_28D1 (see BreakSemantics). */
  breakSemantics?: BreakSemantics;
}

function storedBreakIsPresumed(provenanceInput: unknown, captureMethod: unknown): boolean {
  const provenance = normalizedUpper(provenanceInput);
  return (
    provenance === 'RULE_DEFAULT' ||
    (!['ENTERED', 'CONFIRMED_EXPLICIT_ZERO'].includes(provenance ?? '') &&
      [
        'SCHEDULED_ROSTER_PRESUMPTION',
        'ROSTER_CONFIRMED',
        'COORDINATOR_ON_BEHALF_CLOCK_OUT',
      ].includes(normalizedUpper(captureMethod) ?? ''))
  );
}

/**
 * Read only the server's validated APPROVED correction overlay. Its effective
 * minutes supersede the original punch/roster provenance, including zero.
 * This is evidence consumption, not correction validation or approval.
 */
export function approvedTimesheetCorrectionBreakMinutes(input: {
  storedBreakMinutes: number | null | undefined;
  sourceEvidence?: unknown;
}): number | null {
  const evidence = input.sourceEvidence;
  if (!evidence || typeof evidence !== 'object' || Array.isArray(evidence)) return null;
  const correction = (evidence as Record<string, unknown>).approvedTimesheetCorrection;
  if (!correction || typeof correction !== 'object' || Array.isArray(correction)) return null;
  const marker = correction as Record<string, unknown>;
  return marker.kind === 'TIME_BREAK' &&
    typeof marker.id === 'string' &&
    marker.id.trim().length > 0 &&
    typeof input.storedBreakMinutes === 'number' &&
    Number.isInteger(input.storedBreakMinutes) &&
    input.storedBreakMinutes >= 0
    ? input.storedBreakMinutes
    : null;
}

/**
 * Convert the timesheet's legacy non-null `break_minutes` into the resolver's
 * missing-versus-explicit contract.
 *
 * The table historically stored both "not captured" and "explicitly no break"
 * as zero. Independently approved no-break facts, coordinator entries and
 * server-attributed officer zero suppress an active provider assumption. Positive
 * recorded minutes remain authoritative. When neither exists, an active
 * provider policy returns `undefined`. Only a paid rule may supply its own
 * duration; an unpaid rule requires recorded actual minutes.
 */
export function resolveTimesheetActualBreakInput(input: ResolveTimesheetActualBreakInput): {
  actualBreakMinutes: number | undefined;
  provenance: TimesheetBreakMinutesProvenance;
  policyApplied: boolean;
} {
  if (!isV13BreakSemantics(input.breakSemantics)) {
    return legacy28d1.resolveTimesheetActualBreakInput(input);
  }
  return resolveTimesheetActualBreakInputV13(input);
}

function resolveTimesheetActualBreakInputV13(input: ResolveTimesheetActualBreakInput): {
  actualBreakMinutes: number | undefined;
  provenance: TimesheetBreakMinutesProvenance;
  policyApplied: boolean;
} {
  const correctedMinutes = approvedTimesheetCorrectionBreakMinutes(input);
  if (correctedMinutes !== null) {
    // The correction is newer evidence than facts approved on the source row.
    return {
      actualBreakMinutes: correctedMinutes,
      provenance: 'RECORDED_BREAK_MINUTES',
      policyApplied: false,
    };
  }
  const evidence =
    input.sourceEvidence && typeof input.sourceEvidence === 'object'
      ? (input.sourceEvidence as Record<string, unknown>)
      : {};
  const provenance = input.storedBreakProvenance ?? evidence.breakEntryProvenance;
  const captureMethod = input.storedCaptureMethod ?? evidence.captureMethod;
  const presumed =
    storedBreakIsPresumed(provenance, captureMethod) ||
    evidence.break_source === 'ROSTERED_DEFAULT' ||
    evidence.break_minutes_authority === 'ROSTERED_DEFAULT';
  const explicitEntry =
    evidence.break_source === 'WORKER' ||
    normalizedUpper(provenance) === 'CONFIRMED_EXPLICIT_ZERO' ||
    normalizedUpper(provenance) === 'ENTERED' ||
    evidence.break_source === 'APPROVED_TIME_BREAK_CORRECTION';
  const unresolved =
    !explicitEntry &&
    (presumed ||
      evidence.break_source === 'UNRESOLVED' ||
      evidence.break_minutes_authority === 'UNRESOLVED' ||
      input.storedBreakMinutes == null);
  const storedBreakMinutes = unresolved ? 0 : nonNegativeMinutes(input.storedBreakMinutes, 0);
  const recordedPositive = storedBreakMinutes > 0 && (!presumed || explicitEntry);
  const approvedDuration =
    typeof input.approvedMealBreakDuration === 'number' &&
    Number.isFinite(input.approvedMealBreakDuration)
      ? nonNegativeMinutes(input.approvedMealBreakDuration, 0)
      : null;

  if (input.approvedMealBreakTaken === false) {
    return {
      actualBreakMinutes: 0,
      provenance: 'APPROVED_FACT_NOT_TAKEN',
      policyApplied: false,
    };
  }
  if (input.approvedMealBreakTaken === true) {
    if (approvedDuration !== null) {
      return {
        actualBreakMinutes: approvedDuration,
        provenance: 'APPROVED_FACT_DURATION',
        policyApplied: false,
      };
    }
    if (recordedPositive) {
      return {
        actualBreakMinutes: storedBreakMinutes,
        provenance: 'RECORDED_BREAK_MINUTES',
        policyApplied: false,
      };
    }
    return {
      actualBreakMinutes: undefined,
      provenance: 'APPROVED_FACT_TAKEN_RULE_DEFAULT',
      policyApplied: false,
    };
  }
  if (approvedDuration !== null) {
    return {
      actualBreakMinutes: approvedDuration,
      provenance: 'APPROVED_FACT_DURATION',
      policyApplied: false,
    };
  }
  if (recordedPositive) {
    return {
      actualBreakMinutes: storedBreakMinutes,
      provenance: 'RECORDED_BREAK_MINUTES',
      policyApplied: false,
    };
  }
  if (explicitEntry) {
    return {
      actualBreakMinutes: storedBreakMinutes,
      provenance: 'RECORDED_BREAK_MINUTES',
      policyApplied: false,
    };
  }
  if (unresolved)
    return { actualBreakMinutes: undefined, provenance: 'UNRESOLVED', policyApplied: false };
  if (timesheetMissingBreakPolicyApplies(input.policy, input.eventDate)) {
    return {
      actualBreakMinutes: undefined,
      provenance: 'PROVIDER_POLICY_RULE_DEFAULT',
      policyApplied: true,
    };
  }
  return {
    actualBreakMinutes: undefined,
    provenance: 'UNRESOLVED',
    policyApplied: false,
  };
}

function isExplicitZeroMinutes(value: unknown): boolean {
  const minutes =
    typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(minutes) && minutes === 0;
}

function boolParam(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value.trim().toLowerCase() === 'true') return true;
    if (value.trim().toLowerCase() === 'false') return false;
  }
  return fallback;
}

function sourceMatches(
  params: Record<string, unknown>,
  sourceKind: BreakTreatmentSourceKind,
): boolean {
  const configured = params.sourceKinds;
  if (!Array.isArray(configured) || configured.length === 0) return true;
  return configured.map((s) => normalizedUpper(s)).includes(sourceKind);
}

function categoryMatches(
  params: Record<string, unknown>,
  employeeCategory: string | null,
): boolean {
  const configured = params.employeeCategories ?? params.employeeCategory;
  if (isCategoryScoped(params) && canonicalEmployeeCategory(employeeCategory) === null) {
    return false;
  }
  if (!Array.isArray(configured)) {
    const single = canonicalEmployeeCategory(configured) ?? normalizedUpper(configured);
    const supplied =
      canonicalEmployeeCategory(employeeCategory) ?? normalizedUpper(employeeCategory);
    return !single || single === supplied;
  }
  if (configured.length === 0) return true;
  const supplied = canonicalEmployeeCategory(employeeCategory) ?? normalizedUpper(employeeCategory);
  return configured
    .map((value) => canonicalEmployeeCategory(value) ?? normalizedUpper(value))
    .includes(supplied);
}

function isCategoryScoped(params: Record<string, unknown>): boolean {
  const configured = params.employeeCategories ?? params.employeeCategory;
  return Array.isArray(configured) ? configured.length > 0 : normalizedUpper(configured) !== null;
}

function factValue(facts: BreakTreatmentFactInput[] | null | undefined, factCode: string): unknown {
  const targetCode = normalizedUpper(factCode);
  if (!targetCode) return undefined;
  return (facts ?? []).find(
    (fact) =>
      normalizedUpper(fact.status ?? 'SUPPLIED') !== 'REJECTED' &&
      normalizedUpper(fact.factCode) === targetCode,
  )?.value;
}

function isPositiveFactValue(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value === 'number') return Number.isFinite(value) && value > 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toUpperCase();
    return (
      normalized === 'TRUE' || normalized === 'YES' || normalized === 'Y' || normalized === '1'
    );
  }
  return false;
}

function requiredPositiveFactCodes(params: Record<string, unknown>): string[] {
  const configured = params.requiredPositiveFactCodes ?? params.required_positive_fact_codes;
  if (!Array.isArray(configured)) return [];
  return Array.from(
    new Set(
      configured
        .map((code) => normalizedUpper(code))
        .filter((code): code is string => Boolean(code)),
    ),
  ).sort();
}

function blockIsActive(block: BreakTreatmentRuleBlock): boolean {
  const status = normalizedUpper(block.automation_status ?? 'AUTOMATED');
  return status === 'AUTOMATED' || status === 'MANUAL_ONLY';
}

function blockTreatmentKind(block: BreakTreatmentRuleBlock): BreakTreatmentKind | null {
  const params = block.parameters ?? {};
  const explicit = normalizedUpper(params.treatmentKind ?? params.breakTreatmentKind);
  if (
    explicit === 'UNPAID' ||
    explicit === 'PAID_COUNTS_AS_TIME_WORKED' ||
    explicit === 'PAID_NOT_TIME_WORKED' ||
    explicit === 'NO_BREAK'
  ) {
    return explicit;
  }

  // Only explicit break-treatment blocks may affect payroll break handling.
  // Ordinary-hours blocks can carry roster/contract parameters such as
  // paidHoursPerDay or unpaidMealBreakMinutes, but those are not a source of
  // final break treatment for a worked timesheet.
  if (normalizedUpper(block.block_kind) !== 'BREAK_TREATMENT') return null;
  if (params.paidMealBreakAfterHours !== undefined) return 'PAID_COUNTS_AS_TIME_WORKED';
  if (params.unpaidMealBreakMinutes !== undefined) return 'UNPAID';
  return null;
}

function chooseRuleBlock(
  input: ResolveBreakTreatmentInput,
  scheduledBreakMinutes: number,
): {
  block: BreakTreatmentRuleBlock | null;
  treatmentKind: BreakTreatmentKind;
  scopedCategoryResolutionRequired: boolean;
  belowThresholdRule: BreakTreatmentRuleBlock | null;
} {
  const blocks = (input.ruleBlocks ?? []).filter(blockIsActive);
  const sourceAndTriggerCandidates = blocks
    .map((block) => {
      const params = block.parameters ?? {};
      const treatmentKind = blockTreatmentKind(block);
      if (!treatmentKind) return null;
      if (!sourceMatches(params, input.sourceKind)) return null;
      const requiresExplicitZeroBreak = boolParam(
        params.requiresExplicitZeroBreak ?? params.requires_explicit_zero_break,
        false,
      );
      const hasExplicitZeroBreak =
        input.actualBreakMinutes !== null &&
        input.actualBreakMinutes !== undefined &&
        isExplicitZeroMinutes(input.actualBreakMinutes);
      if (requiresExplicitZeroBreak && !hasExplicitZeroBreak) return null;
      const triggerAfterMinutes = nonNegativeMinutes(
        params.triggerAfterContinuousMinutes ??
          (params.paidMealBreakAfterHours !== undefined
            ? Number(params.paidMealBreakAfterHours) * 60
            : undefined),
        0,
      );
      const belowTrigger = triggerAfterMinutes > 0 && input.rawSpanMinutes <= triggerAfterMinutes;
      const defaultBreakMinutes = nonNegativeMinutes(
        params.defaultBreakMinutes ?? params.paidMealBreakMinutes ?? params.unpaidMealBreakMinutes,
        scheduledBreakMinutes,
      );
      return {
        block,
        // A configured rule below its entitlement trigger must not fall back
        // to an unpaid template deduction. Its identity/category still applies.
        treatmentKind: belowTrigger
          ? Number(input.actualBreakMinutes) > 0 ||
            (input.actualBreakMinutes == null &&
              (isPositiveFactValue(factValue(input.facts, 'MEAL_BREAK_TAKEN')) ||
                Number(factValue(input.facts, 'MEAL_BREAK_DURATION')) > 0))
            ? ('UNPAID' as const)
            : ('NO_BREAK' as const)
          : treatmentKind,
        belowTrigger,
        // Mirrors the DB default for pay_rule_blocks.precedence: omitted values
        // behave like a broad/default rule, while lower configured values win.
        precedence: nonNegativeNumber(block.precedence, 100),
        specificity:
          (requiresExplicitZeroBreak ? 1_000 : 0) +
          (normalizedUpper(block.block_kind) === 'BREAK_TREATMENT' ? 100 : 0) +
          (Array.isArray(params.employeeCategories) || params.employeeCategory ? 10 : 0) +
          (requiredPositiveFactCodes(params).length > 0 ? 5 : 0) +
          (defaultBreakMinutes > 0 ? 1 : 0),
      };
    })
    .filter(
      (
        entry,
      ): entry is {
        block: BreakTreatmentRuleBlock;
        treatmentKind: BreakTreatmentKind;
        specificity: number;
        precedence: number;
        belowTrigger: boolean;
      } => Boolean(entry),
    );

  // A rule that otherwise applies must remain visible when its required
  // positive fact is absent/false. Filtering it out here used to silently
  // fall back to ordinary unpaid-break semantics during FINAL payroll.
  // An eligible paid meal is assumed taken even when capture says zero. Do
  // not let a zero-only missed-meal rule displace that paid entitlement or
  // demand a cause declaration for an assumed break (Paul, 21 October 2026).
  const hasApplicablePaidMeal = sourceAndTriggerCandidates.some(
    (entry) =>
      (entry.treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED' ||
        entry.treatmentKind === 'PAID_NOT_TIME_WORKED') &&
      categoryMatches(entry.block.parameters ?? {}, input.employeeCategory ?? null),
  );
  const candidates = sourceAndTriggerCandidates
    .filter((entry) => {
      const params = entry.block.parameters ?? {};
      const zeroOnly = boolParam(
        params.requiresExplicitZeroBreak ?? params.requires_explicit_zero_break,
        false,
      );
      const premiumKind = normalizedUpper(params.premiumKind);
      return !(
        hasApplicablePaidMeal &&
        zeroOnly &&
        (premiumKind === 'MEAL_BREAK' || premiumKind === 'INTERRUPTED_MEAL_BREAK')
      );
    })
    .filter((entry) =>
      categoryMatches(entry.block.parameters ?? {}, input.employeeCategory ?? null),
    )
    .sort(
      (a, b) =>
        Number(a.belowTrigger) - Number(b.belowTrigger) ||
        b.specificity - a.specificity ||
        a.precedence - b.precedence ||
        a.block.block_code.localeCompare(b.block.block_code),
    );

  const suppliedCategory = canonicalEmployeeCategory(input.employeeCategory);
  const scopedCategoryResolutionRequired =
    suppliedCategory === null &&
    sourceAndTriggerCandidates.some((entry) => isCategoryScoped(entry.block.parameters ?? {}));

  const bestCandidate = candidates[0];
  if (bestCandidate) {
    return {
      block: bestCandidate.block,
      treatmentKind: bestCandidate.treatmentKind,
      scopedCategoryResolutionRequired,
      belowThresholdRule: bestCandidate.belowTrigger ? bestCandidate.block : null,
    };
  }

  return {
    block: null,
    treatmentKind:
      scheduledBreakMinutes > 0 ||
      Number(input.actualBreakMinutes) > 0 ||
      (input.actualBreakMinutes == null &&
        Number(factValue(input.facts, 'MEAL_BREAK_DURATION')) > 0)
        ? 'UNPAID'
        : 'NO_BREAK',
    scopedCategoryResolutionRequired,
    // A known rule with a later trigger establishes that no break is due. An
    // empty rule set, unknown category or missing roster alone cannot do that.
    belowThresholdRule:
      !scopedCategoryResolutionRequired && input.rawSpanMinutes > 0
        ? (blocks
            .slice()
            .sort((a, b) => a.block_code.localeCompare(b.block_code))
            .find((candidate) => {
              const params = candidate.parameters ?? {};
              const threshold = nonNegativeMinutes(
                params.triggerAfterContinuousMinutes ??
                  (params.paidMealBreakAfterHours !== undefined
                    ? Number(params.paidMealBreakAfterHours) * 60
                    : undefined),
                0,
              );
              return (
                blockTreatmentKind(candidate) !== null &&
                !boolParam(
                  params.requiresExplicitZeroBreak ?? params.requires_explicit_zero_break,
                  false,
                ) &&
                sourceMatches(params, input.sourceKind) &&
                categoryMatches(params, input.employeeCategory ?? null) &&
                threshold > 0 &&
                input.rawSpanMinutes <= threshold
              );
            }) ?? null)
        : null,
  };
}

/** Leave has no independently approved worked-time facts; use its own source and leave flags. */
export function resolveLeaveDeductibleBreakMinutes(input: {
  ruleBlocks: BreakTreatmentRuleBlock[];
  employeeCategory: string | null;
  rawSpanMinutes: number;
  breakMinutes: number;
}): number {
  const breakMinutes = nonNegativeMinutes(input.breakMinutes);
  // Leave duration is a valuation of scheduled time, not an assertion of a
  // worked break. Use FORECAST selection, with zero-only actual-fact rules excluded.
  const selected = chooseRuleBlock(
    {
      sourceKind: 'LEAVE_REQUEST',
      employeeCategory: input.employeeCategory,
      rawSpanMinutes: input.rawSpanMinutes,
      scheduledBreakMinutes: breakMinutes,
      ruleBlocks: input.ruleBlocks.filter(
        (block) =>
          normalizedUpper(block.block_kind) === 'BREAK_TREATMENT' &&
          !boolParam(
            block.parameters?.requiresExplicitZeroBreak ??
              block.parameters?.requires_explicit_zero_break,
            false,
          ),
      ),
      finalizationMode: 'FORECAST',
    },
    breakMinutes,
  );
  if (!selected.block) return breakMinutes;
  return selected.treatmentKind === 'NO_BREAK' ||
    selected.treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED' ||
    boolParam(selected.block.parameters?.countsForLeave, false)
    ? 0
    : breakMinutes;
}

function suppliedFactCodes(facts: BreakTreatmentFactInput[] | null | undefined): string[] {
  return Array.from(
    new Set(
      (facts ?? [])
        .filter((fact) => normalizedUpper(fact.status ?? 'SUPPLIED') !== 'REJECTED')
        .map((fact) => normalizedUpper(fact.factCode))
        .filter((code): code is string => Boolean(code)),
    ),
  ).sort();
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
    .join(',')}}`;
}

function stableRuleHash(payload: unknown): string {
  // Change detector only: audit evidence persists the full snapshot, not this
  // small non-cryptographic hash as a content-address or tamper-proof digest.
  const text = stableStringify(payload);
  let hash = 0xcbf29ce484222325n;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= BigInt(text.charCodeAt(i));
    hash = BigInt.asUintN(64, hash * 0x100000001b3n);
  }
  return hash.toString(16).padStart(16, '0');
}

function canonicalFacts(
  facts: BreakTreatmentFactInput[] | null | undefined,
): Array<{ factCode: string; status: string; value: unknown }> {
  return (facts ?? [])
    .map((fact) => ({
      factCode: normalizedUpper(fact.factCode) ?? '',
      status: normalizedUpper(fact.status ?? 'SUPPLIED') ?? 'SUPPLIED',
      value: fact.value === undefined ? null : fact.value,
    }))
    .sort(
      (a, b) =>
        a.factCode.localeCompare(b.factCode) ||
        a.status.localeCompare(b.status) ||
        stableStringify(a.value).localeCompare(stableStringify(b.value)),
    );
}

/**
 * Is this worker paid a flat salary rather than against the award?
 *
 * Deliberately a local, literal check rather than an import: this package must not
 * depend on the web app, and the token is a stored enum value, not a computed one.
 */
function isSalaryFlatPaymentMethodToken(value: unknown): boolean {
  return normalizedUpper(value) === 'SALARY_FLAT';
}

export function resolveBreakTreatment(input: ResolveBreakTreatmentInput): BreakTreatmentSnapshotV1 {
  if (!isV13BreakSemantics(input.breakSemantics)) {
    return legacy28d1.resolveBreakTreatment(
      input as Parameters<typeof legacy28d1.resolveBreakTreatment>[0],
    ) as BreakTreatmentSnapshotV1;
  }
  const correction = input.approvedCorrectionMinutes;
  const effective =
    correction != null
      ? {
          ...input,
          actualBreakMinutes: correction,
          actualBreakMinutesProvenance: 'RECORDED_BREAK_MINUTES' as const,
          facts: [
            ...(input.facts ?? []).filter(
              (f) =>
                !['MEAL_BREAK_TAKEN', 'MEAL_BREAK_DURATION'].includes(
                  f.factCode.trim().toUpperCase(),
                ),
            ),
            { factCode: 'MEAL_BREAK_DURATION', value: correction, status: 'SUPPLIED' },
            { factCode: 'MEAL_BREAK_TAKEN', value: correction > 0, status: 'SUPPLIED' },
          ],
        }
      : input;
  const base = resolveBreakTreatmentV13(effective);
  const context = input.rosterPresumption;
  if (!context || correction != null) return base;
  const original = breakEvidenceObject(context.evidence)?.original;
  if (!originalBreakIsPresumed(original) || originalBreakHasRecordedMinutes(original)) return base;
  // Callers recover different schedules, zero placeholders and fact maps.
  // None is a shared authority for an original presumed capture. SQL evidence
  // independently holds approved meal facts; other required facts stay missing
  // until every path has a deterministic evidence-backed source for them.
  const facts: BreakTreatmentFactInput[] = [];
  const presumedInput: ResolveBreakTreatmentInput = {
    sourceKind: input.sourceKind,
    employeeCategory: input.employeeCategory,
    // Frozen passes fractional minutes; live/rehearsal already round them.
    rawSpanMinutes: nonNegativeMinutes(input.rawSpanMinutes, 0),
    ruleBlocks: input.ruleBlocks,
    finalizationMode: input.finalizationMode,
    breakSemantics: input.breakSemantics,
    paymentMethod: input.paymentMethod,
    scheduledBreakMinutes: rosterScheduledBreakMinutes(context),
    facts,
    actualBreakMinutesProvenance: 'UNRESOLVED',
  };
  const presumedBase = resolveBreakTreatmentV13(
    presumedInput,
    ROSTER_PRESUMED_BREAK_RESOLVER_HASH_VERSION,
  );
  const decision = classifyRosterPresumedBreak({
    ...context,
    breakSemantics: 'V13',
    treatmentKind: presumedBase.treatmentKind,
  });
  if (decision.kind === 'NOT_ELIGIBLE') {
    // NOT_UNPAID is emitted only after all evidence checks pass. A genuine
    // paid/not-applicable outcome can then stand with its other blockers.
    if (decision.reason === 'NOT_UNPAID') return presumedBase;
    // An unknown length does not change the deduction under a paid meal rule,
    // or when the span has not reached the rule trigger. Incomplete roster
    // evidence must not turn that result into BREAK_DURATION_UNRESOLVED. An
    // explicit approved meal fact, approved correction, or recorded-minutes
    // contradiction is an override and stays held. Unpaid treatments stay held
    // so an unknown break is never deducted. V13 only: this path runs only
    // for rebuild-ON semantics (LEGACY_28D1 never reaches the presumption).
    const contradictions = breakEvidenceObject(
      breakEvidenceObject(context.evidence)?.contradictions,
    );
    const explicitAnswer =
      contradictions?.approved_meal_break_fact === true ||
      contradictions?.approved_correction === true ||
      contradictions?.recorded_minutes === true;
    const paidMeal =
      presumedBase.treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED' ||
      presumedBase.treatmentKind === 'PAID_NOT_TIME_WORKED';
    const noBreakDue =
      presumedBase.treatmentKind === 'NO_BREAK' &&
      presumedBase.actualBreakMinutes === 0 &&
      presumedBase.actualBreakMinutesProvenance === 'RULE_NO_BREAK_REQUIRED' &&
      typeof presumedBase.noBreakRequiredRuleBlockId === 'string' &&
      presumedBase.noBreakRequiredRuleBlockId.length > 0;
    const durationIsTheOnlyBlocker = presumedBase.blockers.every(
      (blocker) => blocker.code === 'BREAK_DURATION_UNRESOLVED',
    );
    if (!explicitAnswer && (paidMeal || noBreakDue) && durationIsTheOnlyBlocker) {
      return presumedBase;
    }
    // Unpaid, no-rule, and contradicted explicit answers stay held.
    return {
      ...presumedBase,
      status: 'BLOCKED',
      actualBreakMinutesProvenance: 'UNRESOLVED',
      breakMinutesStatus: 'UNRESOLVED',
      blockers: [
        {
          code: 'BREAK_DURATION_UNRESOLVED',
          message:
            'Break length is unknown. Use Correct actual time to enter the break minutes or confirm that no meal break was taken.',
        },
        ...presumedBase.blockers.filter((b) => b.code !== 'BREAK_DURATION_UNRESOLVED'),
      ],
    };
  }
  facts.push(
    { factCode: 'MEAL_BREAK_DURATION', value: decision.minutes, status: 'SUPPLIED' },
    { factCode: 'MEAL_BREAK_TAKEN', value: true, status: 'SUPPLIED' },
  );
  return resolveBreakTreatmentV13(
    {
      ...presumedInput,
      facts,
      actualBreakMinutes: decision.minutes,
      actualBreakMinutesProvenance: 'ROSTER_PRESUMED',
    },
    ROSTER_PRESUMED_BREAK_RESOLVER_HASH_VERSION,
  );
}

function resolveBreakTreatmentV13(
  input: ResolveBreakTreatmentInput,
  resolverHashVersion = BREAK_TREATMENT_RESOLVER_HASH_VERSION,
): BreakTreatmentSnapshotV1 {
  const rawSpanMinutes = nonNegativeMinutes(input.rawSpanMinutes, 0);
  const salaryFlat = isSalaryFlatPaymentMethodToken(input.paymentMethod);
  const noTenantRuleRecordedZero =
    input.sourceKind === 'TIMESHEET' &&
    input.noTenantBreakRule === true &&
    input.recordedBreakMinutes === 0;

  // Salary-flat short-circuit.
  //
  // Returns BEFORE any rule-block selection, and builds its own rule hash rather
  // than adding `paymentMethod` to the shared one. That is deliberate: folding a
  // new field into the common hash would change the hash of EVERY existing
  // snapshot, marking every stored row stale at once and forcing a mass
  // re-resolution across every tenant. Award rows keep byte-identical hashes.
  //
  // The hash still varies with the inputs that matter here, so a worker converting
  // off flat salary produces a different hash and the row is correctly re-resolved
  // rather than silently keeping a NOT_APPLICABLE it no longer deserves.
  if (salaryFlat || noTenantRuleRecordedZero) {
    return {
      schemaVersion: 1,
      status: 'NOT_APPLICABLE',
      treatmentKind: 'NO_BREAK',
      sourceKind: input.sourceKind,
      ruleBlockId: null,
      ruleBlockCode: null,
      ruleHash: stableRuleHash({
        resolverVersion: `${resolverHashVersion}+${salaryFlat ? 'salary-flat' : 'no-tenant-break-rule'}`,
        sourceKind: input.sourceKind,
        ...(salaryFlat ? { paymentMethod: 'SALARY_FLAT' } : { recordedBreakMinutes: 0 }),
        rawSpanMinutes,
        finalizationMode: input.finalizationMode ?? 'FORECAST',
      }),
      scheduledBreakMinutes: 0,
      actualBreakMinutes: 0,
      ...(noTenantRuleRecordedZero && !salaryFlat
        ? {
            actualBreakMinutesProvenance:
              input.actualBreakMinutes === 0 && input.actualBreakMinutesProvenance !== 'UNRESOLVED'
                ? (input.actualBreakMinutesProvenance ?? 'NONE')
                : 'NONE',
          }
        : {}),
      rawSpanMinutes,
      workedMinutes: rawSpanMinutes,
      payrollPaidBreakMinutes: 0,
      payrollDeductibleBreakMinutes: 0,
      ordinaryTimeWorkedBreakMinutes: 0,
      leaveAccruableBreakMinutes: 0,
      overtimeThresholdBreakMinutes: 0,
      careMinuteExcludedBreakMinutes: 0,
      requiredFactCodes: [],
      suppliedFactCodes: salaryFlat ? [] : suppliedFactCodes(input.facts),
      ...(!salaryFlat ? { derivedFacts: [] } : {}),
      missingFactCodes: [],
      blockers: [],
      segments: salaryFlat
        ? []
        : [
            {
              segmentKind: 'WORKED',
              minutes: rawSpanMinutes,
              countsForPayroll: true,
              countsAsTimeWorked: true,
              countsForLeave: true,
              countsForOvertimeThreshold: true,
              countsForCareMinutes: true,
            },
          ],
    };
  }

  const capturedScheduledBreakMinutes = nonNegativeMinutes(input.scheduledBreakMinutes, 0);
  const actualBreakMinutesProvided =
    input.actualBreakMinutes !== null && input.actualBreakMinutes !== undefined;
  const approvedTaken = isPositiveFactValue(factValue(input.facts, 'MEAL_BREAK_TAKEN'));
  const approvedDuration = factValue(input.facts, 'MEAL_BREAK_DURATION');
  const occurrenceConfirmed =
    approvedTaken ||
    nonNegativeMinutes(approvedDuration, 0) > 0 ||
    input.actualBreakMinutesProvenance === 'APPROVED_FACT_TAKEN_RULE_DEFAULT';
  const capturedActualBreakMinutes = nonNegativeMinutes(
    input.actualBreakMinutes,
    occurrenceConfirmed ? nonNegativeMinutes(approvedDuration, 0) : 0,
  );
  const { block, treatmentKind, scopedCategoryResolutionRequired, belowThresholdRule } =
    chooseRuleBlock(input, capturedScheduledBreakMinutes);
  // Below a paid entitlement trigger, an actual meal is unpaid. Do not inherit
  // the paid block's leave/OT flags or demand a paid-lunch attestation.
  const forcedUnpaid =
    block !== null && treatmentKind === 'UNPAID' && blockTreatmentKind(block) !== 'UNPAID';
  const params = forcedUnpaid
    ? {
        ...block.parameters,
        countsAsTimeWorked: false,
        countsForLeave: false,
        countsForOvertimeThreshold: false,
      }
    : (block?.parameters ?? {});
  const isPaidTreatment =
    treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED' || treatmentKind === 'PAID_NOT_TIME_WORKED';
  const configuredDefaultBreakMinutes = block
    ? nonNegativeMinutes(
        params.defaultBreakMinutes ?? params.paidMealBreakMinutes ?? params.unpaidMealBreakMinutes,
        0,
      )
    : 0;
  // Paid meals retain the configured entitlement for missing/zero capture in
  // FINAL as well as FORECAST. The evidence-only rule applies to unpaid meals.
  const assumedPaidBreak =
    isPaidTreatment && configuredDefaultBreakMinutes > 0 && capturedActualBreakMinutes === 0;
  const shouldApplyConfiguredDefault = assumedPaidBreak;
  const defaultBreakMinutes =
    shouldApplyConfiguredDefault && rawSpanMinutes > 0
      ? Math.min(configuredDefaultBreakMinutes, rawSpanMinutes)
      : configuredDefaultBreakMinutes;
  const scheduledBreakMinutes = shouldApplyConfiguredDefault
    ? defaultBreakMinutes
    : capturedScheduledBreakMinutes;
  const actualBreakMinutes =
    treatmentKind === 'NO_BREAK'
      ? 0
      : shouldApplyConfiguredDefault
        ? defaultBreakMinutes
        : capturedActualBreakMinutes;
  const noBreakRequired =
    belowThresholdRule !== null &&
    treatmentKind === 'NO_BREAK' &&
    actualBreakMinutes === 0 &&
    input.actualBreakMinutesProvenance === 'UNRESOLVED';
  const actualBreakMinutesProvenance: TimesheetBreakMinutesProvenance = shouldApplyConfiguredDefault
    ? 'PROVIDER_POLICY_RULE_DEFAULT'
    : noBreakRequired
      ? 'RULE_NO_BREAK_REQUIRED'
      : !actualBreakMinutesProvided && input.actualBreakMinutesProvenance
        ? input.actualBreakMinutesProvenance
        : actualBreakMinutesProvided
          ? (input.actualBreakMinutesProvenance ?? 'RECORDED_BREAK_MINUTES')
          : occurrenceConfirmed && capturedActualBreakMinutes > 0
            ? 'APPROVED_FACT_DURATION'
            : input.actualBreakMinutesProvenance === 'UNRESOLVED'
              ? 'UNRESOLVED'
              : capturedScheduledBreakMinutes > 0
                ? 'SCHEDULED_BREAK_MINUTES'
                : 'NONE';
  const facts = suppliedFactCodes(input.facts);
  // The paid-rule assumption supplies the paid-lunch fact without a missed-
  // break declaration. Other required facts and explicit refusals still apply.
  const positiveFactCodes = requiredPositiveFactCodes(params).filter(
    (code) => code !== 'PAID_LUNCH_BREAK' || (isPaidTreatment && actualBreakMinutes > 0),
  );
  const paidLunchDerivationProvenance:
    | NonNullable<BreakTreatmentSnapshotV1['derivedFacts']>[number]['provenance']
    | null = assumedPaidBreak
    ? input.actualBreakMinutesProvenance === 'APPROVED_FACT_TAKEN_RULE_DEFAULT'
      ? 'DERIVED_FROM_APPROVED_BREAK_TAKEN_FACT'
      : input.actualBreakMinutesProvenance === 'UNRESOLVED'
        ? 'DERIVED_FROM_RULE_PAID_BREAK_ASSUMPTION'
        : 'DERIVED_FROM_PROVIDER_MISSING_BREAK_POLICY'
    : actualBreakMinutesProvided && capturedActualBreakMinutes > 0
      ? 'DERIVED_FROM_RECORDED_BREAK_MINUTES'
      : actualBreakMinutes > 0 && occurrenceConfirmed
        ? 'DERIVED_FROM_APPROVED_BREAK_TAKEN_FACT'
        : null;
  const derivedFacts: NonNullable<BreakTreatmentSnapshotV1['derivedFacts']> =
    paidLunchDerivationProvenance &&
    positiveFactCodes.includes('PAID_LUNCH_BREAK') &&
    factValue(input.facts, 'PAID_LUNCH_BREAK') === undefined
      ? [
          {
            factCode: 'PAID_LUNCH_BREAK',
            provenance: paidLunchDerivationProvenance,
          },
        ]
      : [];
  const derivedFactCodes = new Set(derivedFacts.map((fact) => fact.factCode));
  const requiredFactCodes = Array.from(
    new Set(
      [
        ...(block?.required_fact_codes ?? []),
        ...((Array.isArray(params.requiredFactCodes) ? params.requiredFactCodes : []) as unknown[]),
        ...positiveFactCodes,
      ]
        .map((code) => normalizedUpper(code))
        .filter((code): code is string => Boolean(code))
        .filter(
          (code) => code !== 'PAID_LUNCH_BREAK' || (isPaidTreatment && actualBreakMinutes > 0),
        ),
    ),
  ).sort();

  const missingFactCodes =
    input.finalizationMode === 'FINAL'
      ? requiredFactCodes.filter((code) => !facts.includes(code) && !derivedFactCodes.has(code))
      : [];
  const unsatisfiedPositiveFactCodes = positiveFactCodes.filter(
    (code) => !derivedFactCodes.has(code) && !isPositiveFactValue(factValue(input.facts, code)),
  );

  const blockers: BreakTreatmentSnapshotV1['blockers'] = [];
  const breakMinutesUnresolved =
    !isPaidTreatment &&
    actualBreakMinutes === 0 &&
    !noBreakRequired &&
    (occurrenceConfirmed ||
      input.actualBreakMinutesProvenance === 'UNRESOLVED' ||
      input.actualBreakMinutesProvenance === 'PROVIDER_POLICY_RULE_DEFAULT' ||
      (!actualBreakMinutesProvided && treatmentKind === 'UNPAID')) &&
    input.actualBreakMinutesProvenance !== 'APPROVED_FACT_NOT_TAKEN';
  if (breakMinutesUnresolved) {
    blockers.push({
      code: 'BREAK_DURATION_UNRESOLVED',
      message:
        'Break length is unknown. Use Correct actual time to enter the break minutes or confirm that no meal break was taken.',
    });
  }
  if (missingFactCodes.length > 0) {
    blockers.push({
      code: 'BREAK_TREATMENT_FACTS_MISSING',
      message: `Required break-treatment facts missing: ${missingFactCodes.join(', ')}.`,
    });
  }
  if (input.finalizationMode === 'FINAL' && unsatisfiedPositiveFactCodes.length > 0) {
    blockers.push({
      code: 'BREAK_TREATMENT_POSITIVE_FACT_REQUIRED',
      message: `Break-treatment facts must be explicitly positive: ${unsatisfiedPositiveFactCodes.join(', ')}.`,
    });
  }
  if (input.finalizationMode === 'FINAL' && scopedCategoryResolutionRequired) {
    blockers.push({
      code: 'BREAK_TREATMENT_EMPLOYEE_CATEGORY_UNRESOLVED',
      message:
        'Employee category is missing or unrecognised while category-scoped break rules apply.',
    });
  }
  const countsAsTimeWorked =
    treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED' || boolParam(params.countsAsTimeWorked, false);
  const countsForLeave =
    treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED' || boolParam(params.countsForLeave, false);
  const countsForOvertimeThreshold =
    treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED' ||
    boolParam(params.countsForOvertimeThreshold, countsAsTimeWorked);

  const payrollPaidBreakMinutes =
    treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED' || treatmentKind === 'PAID_NOT_TIME_WORKED'
      ? actualBreakMinutes
      : 0;
  const payrollDeductibleBreakMinutes = treatmentKind === 'UNPAID' ? actualBreakMinutes : 0;
  const workedMinutes = Math.max(0, rawSpanMinutes - actualBreakMinutes);
  const paidBreakSegment: BreakTreatmentSegment | null =
    payrollPaidBreakMinutes > 0
      ? {
          segmentKind:
            treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED'
              ? 'PAID_BREAK_COUNTS_AS_TIME_WORKED'
              : 'PAID_BREAK_NOT_TIME_WORKED',
          minutes: payrollPaidBreakMinutes,
          countsForPayroll: true,
          countsAsTimeWorked,
          countsForLeave,
          countsForOvertimeThreshold,
          countsForCareMinutes: false,
        }
      : null;
  const unpaidBreakSegment: BreakTreatmentSegment | null =
    payrollDeductibleBreakMinutes > 0
      ? {
          segmentKind: 'UNPAID_BREAK',
          minutes: payrollDeductibleBreakMinutes,
          countsForPayroll: false,
          countsAsTimeWorked: false,
          countsForLeave: false,
          countsForOvertimeThreshold: false,
          countsForCareMinutes: false,
        }
      : null;

  return {
    schemaVersion: 1,
    status:
      blockers.length > 0
        ? 'BLOCKED'
        : noBreakRequired
          ? 'NOT_APPLICABLE'
          : input.finalizationMode !== 'FINAL' &&
              (unsatisfiedPositiveFactCodes.length > 0 || scopedCategoryResolutionRequired)
            ? 'AMBIGUOUS'
            : scheduledBreakMinutes > 0 || actualBreakMinutes > 0
              ? 'RESOLVED'
              : 'NOT_APPLICABLE',
    treatmentKind,
    sourceKind: input.sourceKind,
    ruleBlockId: block?.id ?? null,
    ruleBlockCode: block?.block_code ?? null,
    ...(noBreakRequired ? { noBreakRequiredRuleBlockId: belowThresholdRule?.id ?? null } : {}),
    ruleHash: stableRuleHash({
      resolverVersion: resolverHashVersion,
      sourceKind: input.sourceKind,
      employeeCategory:
        canonicalEmployeeCategory(input.employeeCategory) ??
        normalizedUpper(input.employeeCategory),
      rawSpanMinutes,
      capturedScheduledBreakMinutes,
      actualBreakMinutesProvided,
      actualBreakMinutes,
      ...((assumedPaidBreak || input.actualBreakMinutesProvenance) &&
      actualBreakMinutesProvenance !== 'NONE'
        ? { actualBreakMinutesProvenance }
        : {}),
      finalizationMode: input.finalizationMode ?? 'FORECAST',
      facts: canonicalFacts(input.facts),
      block: block
        ? {
            id: block.id ?? null,
            blockCode: block.block_code,
            blockKind: block.block_kind,
            automationStatus: block.automation_status ?? null,
            precedence: block.precedence ?? null,
            parameters: params,
            requiredFactCodes: block.required_fact_codes ?? [],
            exportComponentCodes: block.export_component_codes ?? [],
          }
        : null,
      treatmentKind,
      requiredFactCodes,
      derivedFacts,
      missingFactCodes,
      unsatisfiedPositiveFactCodes,
      scopedCategoryResolutionRequired,
      ...(noBreakRequired ? { belowThresholdRule } : {}),
    }),
    scheduledBreakMinutes,
    actualBreakMinutes,
    actualBreakMinutesProvenance,
    ...(breakMinutesUnresolved ? { breakMinutesStatus: 'UNRESOLVED' as const } : {}),
    rawSpanMinutes,
    workedMinutes,
    payrollPaidBreakMinutes,
    payrollDeductibleBreakMinutes,
    ordinaryTimeWorkedBreakMinutes: countsAsTimeWorked ? actualBreakMinutes : 0,
    leaveAccruableBreakMinutes: countsForLeave ? actualBreakMinutes : 0,
    overtimeThresholdBreakMinutes: countsForOvertimeThreshold ? actualBreakMinutes : 0,
    careMinuteExcludedBreakMinutes: actualBreakMinutes,
    requiredFactCodes,
    suppliedFactCodes: facts,
    derivedFacts,
    missingFactCodes,
    blockers,
    segments: [
      {
        segmentKind: 'WORKED',
        minutes: workedMinutes,
        countsForPayroll: true,
        countsAsTimeWorked: true,
        countsForLeave: true,
        countsForOvertimeThreshold: true,
        countsForCareMinutes: true,
      },
      ...(paidBreakSegment ? [paidBreakSegment] : []),
      ...(unpaidBreakSegment ? [unpaidBreakSegment] : []),
    ],
  };
}

export function payrollBreakMinutesForEngine(
  rawBreakMinutes: number | null | undefined,
  breakTreatment: BreakTreatmentSnapshotV1 | null | undefined,
  breakSemantics?: BreakSemantics,
): number {
  if (!isV13BreakSemantics(breakSemantics)) {
    return legacy28d1.payrollBreakMinutesForEngine(
      rawBreakMinutes,
      breakTreatment as Parameters<typeof legacy28d1.payrollBreakMinutesForEngine>[1],
    );
  }
  if (!breakTreatment) return nonNegativeMinutes(rawBreakMinutes, 0);
  if (breakTreatment.status === 'BLOCKED' || breakTreatment.status === 'AMBIGUOUS') {
    throw new Error(
      breakTreatment.blockers[0]?.message ??
        'Break treatment is unavailable. Review the break evidence before pricing.',
    );
  }
  if (breakTreatment.treatmentKind === 'PAID_NOT_TIME_WORKED') {
    return breakTreatment.payrollDeductibleBreakMinutes + breakTreatment.payrollPaidBreakMinutes;
  }
  return breakTreatment.payrollDeductibleBreakMinutes;
}

/**
 * LEGACY_28D1 hours: 28d1 call sites read the stored `total_hours` column
 * (`Number(...)` / `parseFloat(...)`). Non-numeric or missing values are null;
 * callers keep their 28d1 `?? 0` / `?? NaN` fallbacks. The timesheet display
 * normaliser does not use this: it keeps the raw stored value exactly as 28d1.
 */
export function legacyStoredTimesheetHours(totalHours: unknown): number | null {
  if (typeof totalHours === 'number') return Number.isFinite(totalHours) ? totalHours : null;
  if (typeof totalHours === 'string' && totalHours.trim() !== '') {
    const parsed = Number(totalHours);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Read capture hours without turning a planned break into lost paid time.
 * Authoritative approved break snapshots override a planning marker; old default
 * snapshots do not. Immutable pay-run items remain a separate authority.
 */
export function timesheetHoursForBreakEvidence(
  row: {
    roster_break_context?: RosterBreakContext;
    break_minutes?: unknown;
    total_hours?: unknown;
    actual_start?: unknown;
    actual_end?: unknown;
    source_evidence?: unknown;
    break_treatment_snapshot?: unknown;
  },
  basis: 'WORKED' | 'LEAVE' = 'WORKED',
  breakSemantics?: BreakSemantics,
): number | null {
  if (!isV13BreakSemantics(breakSemantics)) return legacyStoredTimesheetHours(row.total_hours);
  const stored =
    typeof row.total_hours === 'number' ||
    (typeof row.total_hours === 'string' && row.total_hours.trim() !== '')
      ? Number(row.total_hours)
      : NaN;
  const evidence =
    row.source_evidence && typeof row.source_evidence === 'object'
      ? (row.source_evidence as Record<string, unknown>)
      : {};
  const presumed = originalBreakIsPresumed(evidence) && !originalBreakHasRecordedMinutes(evidence);
  if (!presumed) return Number.isFinite(stored) && stored >= 0 ? stored : null;
  const start = typeof row.actual_start === 'string' ? Date.parse(row.actual_start) : NaN;
  const end = typeof row.actual_end === 'string' ? Date.parse(row.actual_end) : NaN;
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  const span = Math.round((end - start) / 60_000);
  const snapshot =
    row.break_treatment_snapshot && typeof row.break_treatment_snapshot === 'object'
      ? (row.break_treatment_snapshot as Record<string, unknown>)
      : {};
  if (
    row.roster_break_context &&
    snapshot.treatmentKind === 'UNPAID' &&
    approvedTimesheetCorrectionBreakMinutes({
      storedBreakMinutes: typeof row.break_minutes === 'number' ? row.break_minutes : null,
      sourceEvidence: row.source_evidence,
    }) === null
  ) {
    const decision = classifyRosterPresumedBreak({
      ...row.roster_break_context,
      breakSemantics: 'V13',
      treatmentKind: 'UNPAID',
    });
    // Use accepted scheduled minutes even when the original stored zero/missing.
    if (decision.kind === 'ROSTER_PRESUMED') return Math.max(0, span - decision.minutes) / 60;
  }
  const actualProvenance = normalizedUpper(snapshot.actualBreakMinutesProvenance);
  if (
    snapshot.status === 'RESOLVED' &&
    snapshot.rawSpanMinutes === span &&
    actualProvenance?.startsWith('APPROVED_FACT_')
  ) {
    const worked = Number(snapshot.workedMinutes);
    const breakContribution = Number(
      basis === 'LEAVE'
        ? snapshot.leaveAccruableBreakMinutes
        : snapshot.ordinaryTimeWorkedBreakMinutes,
    );
    if (Number.isFinite(worked) && Number.isFinite(breakContribution))
      return (worked + breakContribution) / 60;
  }
  return span / 60;
}

/** SQL counterpart for existing tenant-scoped report queries; aliases are source constants only. */
export function timesheetHoursForBreakEvidenceSql(
  alias: string,
  breakSemantics?: BreakSemantics,
): string {
  if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(alias)) throw new Error('Invalid timesheet SQL alias');
  // 28d1 report queries read the stored column directly.
  if (!isV13BreakSemantics(breakSemantics)) return `${alias}.total_hours`;
  const provenance = `upper(btrim(coalesce(${alias}.source_evidence->>'breakEntryProvenance', '')))`;
  const capture = `upper(btrim(coalesce(${alias}.source_evidence->>'captureMethod', '')))`;
  const snapshot = `${alias}.break_treatment_snapshot`;
  const span = `round(extract(epoch FROM (${alias}.actual_end - ${alias}.actual_start)) / 60)`;
  const approvedSnapshot = `(${snapshot}->>'status' = 'RESOLVED'
    AND ${snapshot}->>'rawSpanMinutes' = (${span})::text
    AND left(upper(coalesce(${snapshot}->>'actualBreakMinutesProvenance', '')), 14) = 'APPROVED_FACT_'
    AND jsonb_typeof(${snapshot}->'workedMinutes') = 'number'
    AND jsonb_typeof(${snapshot}->'ordinaryTimeWorkedBreakMinutes') = 'number')`;
  return `(CASE WHEN ${provenance} = 'RULE_DEFAULT' OR
      (${provenance} NOT IN ('ENTERED', 'CONFIRMED_EXPLICIT_ZERO') AND
       ${capture} IN ('SCHEDULED_ROSTER_PRESUMPTION', 'ROSTER_CONFIRMED', 'COORDINATOR_ON_BEHALF_CLOCK_OUT'))
    THEN CASE WHEN ${alias}.actual_end > ${alias}.actual_start
      THEN CASE WHEN ${approvedSnapshot}
        THEN ((${snapshot}->>'workedMinutes')::numeric +
          (${snapshot}->>'ordinaryTimeWorkedBreakMinutes')::numeric) / 60
        ELSE (${span}) / 60 END
      ELSE NULL END
    ELSE ${alias}.total_hours END)`;
}
